/**
 * ItineraryView — V3-native itinerary viewer.
 *
 * Slim orchestrator that composes sub-components for rendering V3 engine output.
 * Falls back to the legacy view for non-V3 itineraries.
 */
import React, { useState, useCallback, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../../../utils/toast';
import { useUser } from '../../../../context/UserContext';
import useItineraryV3 from '../../../../hooks/useItineraryV3';
import ItineraryToolbar from './v3/ItineraryToolbar';
import ItineraryDayList from './v3/ItineraryDayList';
import EditDayModal from './v3/EditDayModal';
import { Loader2, AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';

// Lazy-load the legacy view for non-V3 itineraries
const LegacyItineraryView = React.lazy(() => import('./ItineraryView.v2'));

export default function ItineraryView() {
  const { id } = useParams();
  const { user } = useUser();
  const { data, isV3, loading, error, refetch } = useItineraryV3(id);

  // Edit modal state
  const [editingDay, setEditingDay] = useState(null);

  // Regenerate state
  const [regenerating, setRegenerating] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  // Trip name (local state for optimistic update)
  const [tripNameOverride, setTripNameOverride] = useState(null);
  const tripName = tripNameOverride ?? data?.tripName ?? 'Itinerary';

  const handleRegenerate = useCallback(async () => {
    try {
      setRegenerating(true);
      toast.info('Regenerating itinerary... This may take a moment.');
      const endpoint = `${import.meta.env.VITE_API_URL}/api/itineraries/${id}/regenerate`;
      const response = await axios.post(endpoint, {}, { withCredentials: true });
      const ok = response?.status >= 200 && response?.status < 300;
      if (ok) {
        toast.success('Itinerary regenerated successfully!');
        setShowRegenerateConfirm(false);
        // Refetch after a brief delay to let the backend finish writing
        setTimeout(() => refetch(), 1500);
      } else {
        throw new Error(response?.data?.message || 'Invalid response');
      }
    } catch (err) {
      console.error('Regenerate error:', err);
      const msg =
        err.response?.data?.message ||
        (err.response?.status === 403
          ? "You don't have permission to regenerate this itinerary"
          : 'Failed to regenerate. Please try again.');
      toast.error(msg);
    } finally {
      setRegenerating(false);
      setShowRegenerateConfirm(false);
    }
  }, [id, refetch]);

  // --- Loading state ---
  if (loading) {
    return (
      <div className="flex flex-col gap-3 justify-center items-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        <p className="text-sm text-gray-500">Loading itinerary...</p>
      </div>
    );
  }

  // --- Error state ---
  if (error) {
    return (
      <div className="flex flex-col gap-4 justify-center items-center min-h-[60vh]">
        <AlertCircle className="w-10 h-10 text-red-400" />
        <p className="text-sm text-gray-700">{error}</p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="flex gap-1.5 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" /> Go Back
          </button>
          <button
            type="button"
            onClick={() => refetch()}
            className="flex gap-1.5 items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  // --- Non-V3 fallback: render legacy view ---
  if (!isV3) {
    return (
      <Suspense
        fallback={
          <div className="flex justify-center items-center min-h-[60vh]">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        }
      >
        <LegacyItineraryView />
      </Suspense>
    );
  }

  // --- V3 itinerary ---
  const { days, magicMessages, tripWarnings = [], ledger } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toolbar */}
      <ItineraryToolbar
        tripName={tripName}
        tripId={id}
        onTripNameChange={setTripNameOverride}
        onRegenerate={() => setShowRegenerateConfirm(true)}
        regenerating={regenerating}
      />

      {/* Main content */}
      <div className="px-4 py-6 mx-auto max-w-5xl sm:px-6">
        {/* Trip-level warnings */}
        {tripWarnings.length > 0 && (
          <div className="p-3 mb-4 rounded-lg border border-amber-200 bg-amber-50">
            <ul className="space-y-1">
              {tripWarnings.map((w, i) => (
                <li key={i} className="flex gap-2 items-start text-xs text-amber-700">
                  <AlertCircle className="flex-shrink-0 mt-0.5 w-3 h-3 text-amber-500" />
                  <span>{typeof w === 'string' ? w : w.message || JSON.stringify(w)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Trip summary bar */}
        <div className="flex flex-wrap gap-3 items-center mb-6">
          <span className="text-xs text-gray-500">
            {days.length} day{days.length !== 1 ? 's' : ''}
          </span>
          {ledger?.usedRideCount > 0 && (
            <span className="text-xs text-gray-500">
              {ledger.usedRideCount} ride{ledger.usedRideCount !== 1 ? 's' : ''}
            </span>
          )}
          {ledger?.usedDiningCount > 0 && (
            <span className="text-xs text-gray-500">
              {ledger.usedDiningCount} dining reservation{ledger.usedDiningCount !== 1 ? 's' : ''}
            </span>
          )}
          {ledger?.usedShowCount > 0 && (
            <span className="text-xs text-gray-500">
              {ledger.usedShowCount} show{ledger.usedShowCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Day list */}
        <ItineraryDayList
          days={days}
          onEditDay={(day) => setEditingDay(day)}
        />
      </div>

      {/* Edit Day Modal */}
      <EditDayModal
        day={editingDay}
        tripId={id}
        isOpen={!!editingDay}
        onClose={() => setEditingDay(null)}
        onSaved={() => {
          setEditingDay(null);
          refetch();
        }}
      />

      {/* Regenerate Confirmation Modal */}
      {showRegenerateConfirm && (
        <div
          className="flex fixed inset-0 z-modal justify-center items-center p-4 bg-black/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.target === e.currentTarget && setShowRegenerateConfirm(false)}
        >
          <div className="w-full max-w-md p-6 bg-white rounded-xl shadow-xl">
            <div className="flex gap-3 items-start mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <RefreshCw className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Regenerate Itinerary</h3>
                <p className="mt-1 text-sm text-gray-600">
                  This will regenerate the entire itinerary using the latest engine. Any manual edits
                  will be replaced. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowRegenerateConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRegenerate}
                disabled={regenerating}
                className="flex gap-1.5 items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50"
              >
                {regenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {regenerating ? 'Regenerating...' : 'Regenerate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
